import { TranslateModule } from '@ngx-translate/core';
import { LogService } from '@services/util/log.service';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CheckinFilter } from '../../models/checkin-filter.model';

@Component({
  selector: 'app-checkins-filters',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    DatePickerModule,
    CardModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    AccordionModule,
    SelectModule,
    SelectButtonModule
  ],
  template: `
    <div class="mb-4">
      <p-accordion
        [multiple]="false"
        [activeIndex]="isExpanded ? 0 : -1"
        (onClose)="isExpanded = false"
        (onOpen)="isExpanded = true"
      >
        <p-accordion-panel value="0">
          <p-accordion-header>
            <div class="flex items-center gap-2">
              <i class="pi pi-filter"></i>
              <span>{{ 'filters.title' | translate }}</span>
              @if (hasActiveFilters() && countActiveFilters() > 0) {
                <span class="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-white">{{
                  countActiveFilters()
                }}</span>
              }
            </div>
          </p-accordion-header>
          <p-accordion-content>
            <form
              [formGroup]="filterForm"
              class="flex flex-col gap-4"
            >
              <!-- Name Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'checkins.filters.name' | translate }}</h5>
                <div>
                  <p-inputGroup class="w-full md:w-2/3 lg:w-1/2">
                    <p-inputGroupAddon>
                      <i class="pi pi-search"></i>
                    </p-inputGroupAddon>
                    <input
                      type="text"
                      pInputText
                      formControlName="name"
                      class="w-full"
                      [placeholder]="'filters.searchPlaceholder' | translate"
                    />
                    @if (filterForm.get('name')?.value) {
                      <p-inputGroupAddon>
                        <button
                          type="button"
                          class="p-inputgroup-addon cursor-pointer"
                          (click)="clearFilterField('name')"
                        >
                          <i class="pi pi-times"></i>
                        </button>
                      </p-inputGroupAddon>
                    }
                  </p-inputGroup>
                </div>
              </div>

              <!-- Email Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'checkins.filters.email' | translate }}</h5>
                <div>
                  <p-inputGroup class="w-full md:w-2/3 lg:w-1/2">
                    <p-inputGroupAddon>
                      <i class="pi pi-search"></i>
                    </p-inputGroupAddon>
                    <input
                      type="text"
                      pInputText
                      formControlName="email"
                      class="w-full"
                      [placeholder]="'filters.searchPlaceholder' | translate"
                    />
                    @if (filterForm.get('email')?.value) {
                      <p-inputGroupAddon>
                        <button
                          type="button"
                          class="p-inputgroup-addon cursor-pointer"
                          (click)="clearFilterField('email')"
                        >
                          <i class="pi pi-times"></i>
                        </button>
                      </p-inputGroupAddon>
                    }
                  </p-inputGroup>
                </div>
              </div>

              <!-- Country Code Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'checkins.filters.countryCode' | translate }}</h5>
                <div>
                  <p-inputGroup class="w-full md:w-2/3 lg:w-1/2">
                    <p-inputGroupAddon>
                      <i class="pi pi-search"></i>
                    </p-inputGroupAddon>
                    <input
                      type="text"
                      pInputText
                      formControlName="countryCode"
                      class="w-full"
                      [placeholder]="'filters.searchPlaceholder' | translate"
                    />
                    @if (filterForm.get('countryCode')?.value) {
                      <p-inputGroupAddon>
                        <button
                          type="button"
                          class="p-inputgroup-addon cursor-pointer"
                          (click)="clearFilterField('countryCode')"
                        >
                          <i class="pi pi-times"></i>
                        </button>
                      </p-inputGroupAddon>
                    }
                  </p-inputGroup>
                </div>
              </div>

              <!-- City Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'checkins.filters.city' | translate }}</h5>
                <div>
                  <p-inputGroup class="w-full md:w-2/3 lg:w-1/2">
                    <p-inputGroupAddon>
                      <i class="pi pi-search"></i>
                    </p-inputGroupAddon>
                    <input
                      type="text"
                      pInputText
                      formControlName="city"
                      class="w-full"
                      [placeholder]="'filters.searchPlaceholder' | translate"
                    />
                    @if (filterForm.get('city')?.value) {
                      <p-inputGroupAddon>
                        <button
                          type="button"
                          class="p-inputgroup-addon cursor-pointer"
                          (click)="clearFilterField('city')"
                        >
                          <i class="pi pi-times"></i>
                        </button>
                      </p-inputGroupAddon>
                    }
                  </p-inputGroup>
                </div>
              </div>

              <!-- Newsletter Signup Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'checkins.filters.newsletterSignup' | translate }}</h5>
                <div>
                  <p-selectButton
                    formControlName="newsletterSignup"
                    [options]="newsletterOptions"
                    optionLabel="label"
                    optionValue="value"
                  >
                    <ng-template
                      pTemplate="item"
                      let-item
                    >
                      {{ item.label | translate }}
                    </ng-template>
                  </p-selectButton>
                </div>
              </div>

              <!-- Created At Date Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'checkins.filters.createdAt' | translate }}</h5>
                <div class="flex flex-col gap-3 sm:flex-row">
                  <div class="w-full sm:w-1/3 lg:w-1/4">
                    <p-select
                      formControlName="createdAtOperator"
                      [options]="dateOperatorOptions"
                      optionLabel="label"
                      optionValue="value"
                      styleClass="w-full"
                      appendTo="body"
                    >
                      <ng-template
                        pTemplate="selectedItem"
                        let-item
                      >
                        {{ item.label | translate }}
                      </ng-template>
                      <ng-template
                        pTemplate="item"
                        let-option
                      >
                        {{ option.label | translate }}
                      </ng-template>
                    </p-select>
                  </div>
                  <div class="w-full sm:w-2/3 lg:w-1/3">
                    <p-datepicker
                      formControlName="createdAt"
                      styleClass="w-full"
                      [showIcon]="true"
                      [dateFormat]="dateFormat"
                      [placeholder]="'filters.datePlaceholder' | translate"
                    >
                    </p-datepicker>
                  </div>
                </div>
              </div>

              <!-- Filter Buttons -->
              <div class="flex justify-end">
                <div class="flex gap-3">
                  <button
                    pButton
                    type="button"
                    [label]="'common.reset' | translate"
                    class="p-button-outlined"
                    (click)="resetFilters()"
                  ></button>
                  <button
                    pButton
                    type="button"
                    [label]="'common.apply' | translate"
                    class="p-button-primary"
                    (click)="applyFilters()"
                  ></button>
                </div>
              </div>
            </form>
          </p-accordion-content>
        </p-accordion-panel>
      </p-accordion>
    </div>
  `
})
export class CheckinsFiltersComponent implements OnInit {
  /**
   * Active filter to populate form values for editing
   */
  @Input() activeFilter: CheckinFilter | undefined;

  /**
   * Event emitted when filters are applied
   */
  @Output() filterChange = new EventEmitter<CheckinFilter>();

  /**
   * Date format string
   */
  dateFormat = 'yy-mm-dd';

  /**
   * Filter form group
   */
  filterForm!: FormGroup;

  /**
   * Options for date filter operators
   */
  dateOperatorOptions = [
    { label: 'filters.exact', value: 'exact' },
    { label: 'filters.before', value: 'before' },
    { label: 'filters.after', value: 'after' },
    { label: 'filters.strictlyBefore', value: 'strictly_before' },
    { label: 'filters.strictlyAfter', value: 'strictly_after' }
  ];

  /**
   * Options for newsletter signup filter
   */
  newsletterOptions = [
    { label: 'checkins.filters.newsletterOptions.all', value: null },
    { label: 'common.yes', value: 1 },
    { label: 'common.no', value: 0 }
  ];

  /**
   * Controls the expanded/collapsed state of the filters accordion
   */
  isExpanded = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly log: LogService
  ) {}

  /**
   * Initialize form
   */
  ngOnInit(): void {
    this.initForm();
    this.populateForm();
  }

  /**
   * Initialize filter form
   */
  private initForm(): void {
    this.filterForm = this.fb.group({
      name: [null],
      email: [null],
      countryCode: [null],
      city: [null],
      // Explicitly set null to show 'All' option selected by default
      newsletterSignup: [null],
      createdAt: [null],
      createdAtOperator: ['exact']
    });

    // Force 'All' option to be selected initially
    // This is needed because PrimeNG selectButton doesn't always display the initial null value correctly
    setTimeout(() => {
      this.filterForm.get('newsletterSignup')?.setValue(null);
    });
  }

  /**
   * Populate form with active filter values
   */
  private populateForm(): void {
    if (this.activeFilter) {
      this.filterForm.patchValue({
        name: this.activeFilter.name,
        email: this.activeFilter.email,
        countryCode: this.activeFilter.countryCode,
        city: this.activeFilter.city,
        newsletterSignup: this.activeFilter.newsletterSignup
      });

      if (this.activeFilter.createdAt) {
        this.filterForm.patchValue({
          createdAt: this.activeFilter.createdAt.date,
          createdAtOperator: this.activeFilter.createdAt.operator
        });
      }
    }
  }

  /**
   * Apply filter values and emit filter change event
   */
  applyFilters(): void {
    const formValue = this.filterForm.value;
    const filter: CheckinFilter = {};

    if (formValue.name) {
      filter.name = formValue.name;
    }

    if (formValue.email) {
      filter.email = formValue.email;
    }

    if (formValue.countryCode) {
      filter.countryCode = formValue.countryCode;
    }

    if (formValue.city) {
      filter.city = formValue.city;
    }

    if (formValue.newsletterSignup !== null) {
      filter.newsletterSignup = formValue.newsletterSignup;
    }

    if (formValue.createdAt) {
      filter.createdAt = {
        date: formValue.createdAt,
        operator: formValue.createdAtOperator
      };
    }

    this.log.debug('Applied checkin filters:', filter);
    this.filterChange.emit(filter);
  }

  /**
   * Reset filter values and emit empty filter
   */
  resetFilters(): void {
    this.filterForm.reset({
      createdAtOperator: 'exact',
      newsletterSignup: null
    });
    this.filterChange.emit({});
  }

  /**
   * Checks if any filters are currently active
   * @returns true if any filter has a value, false otherwise
   */
  hasActiveFilters(): boolean {
    const formValue = this.filterForm?.value;
    if (!formValue) return false;

    return !!(
      formValue.name ||
      formValue.email ||
      formValue.countryCode ||
      formValue.city ||
      formValue.newsletterSignup === 0 ||
      formValue.newsletterSignup === 1 ||
      formValue.createdAt
    );
  }

  /**
   * Counts the number of active filters
   * @returns number of active filters
   */
  countActiveFilters(): number {
    const formValue = this.filterForm?.value;
    if (!formValue) return 0;

    let count = 0;
    if (formValue.name) count++;
    if (formValue.email) count++;
    if (formValue.countryCode) count++;
    if (formValue.city) count++;
    // Only count newsletterSignup if it's 0 or 1 (Yes/No), not if it's null (All)
    if (formValue.newsletterSignup === 0 || formValue.newsletterSignup === 1) count++;
    if (formValue.createdAt) count++;

    return count;
  }

  /**
   * Clear a specific filter field
   * @param fieldName The name of the form control to clear
   */
  clearFilterField(fieldName: string): void {
    this.filterForm.get(fieldName)?.setValue(null);
  }
}
